import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Gauge, Hand } from "lucide-react";

import { api } from "../../api/client";
import { cn } from "@/lib/utils";

/**
 * Onglets de cockpit routés (variante B, owner 2026-07-31) : « Autopilote »
 * (/dashboard, cyan) / « Commandes » (/manual, bleu), chacun avec son COMPTE
 * en badge. Rendus en tête des deux pages cockpit — la nav garde une entrée
 * unique « Cockpit ». Onglets = liens : deep-linking, bouton retour et titre
 * d'onglet conservés.
 */
export default function CockpitTabs() {
  const [autoAccount, setAutoAccount] = useState<string | null>(null);
  const [manualAccount, setManualAccount] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .modeStatus()
      .then((m) => {
        if (!cancelled) setAutoAccount(m.accountId);
      })
      .catch(() => {});
    api
      .manualStatus()
      .then((s) => {
        if (!cancelled) setManualAccount(s.accountId);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex gap-1.5" role="tablist" aria-label="Cockpits" data-testid="cockpit-tabs">
      <Tab
        to="/dashboard"
        label="Autopilote"
        account={autoAccount}
        icon={<Gauge className="size-4" aria-hidden />}
        activeClass="text-accent-neon bg-accent-neon/10 border-accent-neon/45 shadow-[inset_0_2px_0_var(--color-accent-neon)]"
        testId="cockpit-tab-auto"
      />
      <Tab
        to="/manual"
        label="Commandes"
        account={manualAccount}
        icon={<Hand className="size-4" aria-hidden />}
        activeClass="text-info bg-info-soft border-info/45 shadow-[inset_0_2px_0_var(--color-info)]"
        testId="cockpit-tab-manual"
      />
    </div>
  );
}

function Tab({
  to,
  label,
  account,
  icon,
  activeClass,
  testId,
}: {
  to: string;
  label: string;
  account: string | null;
  icon: React.ReactNode;
  activeClass: string;
  testId: string;
}) {
  return (
    <NavLink
      to={to}
      role="tab"
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2 rounded-t-lg border border-b-0 px-4 py-2 text-sm font-semibold transition-colors",
          "outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
          isActive ? activeClass : "border-border text-muted-foreground hover:text-foreground",
        )
      }
    >
      {icon}
      {label}
      <span className="rounded bg-black/25 px-1.5 py-px font-mono text-[11px] font-bold">
        {account ?? "—"}
      </span>
    </NavLink>
  );
}
